import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function SponsorCheckoutSuccessPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 12) {
        setStatus("failed");
        return;
      }
      attempts += 1;
      try {
        const { data: d } = await api.get(`/payments/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid") {
          setStatus("paid");
          toast.success("Payment complete — sponsor onboarded!");
          return;
        }
        if (d.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        // keep polling
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (status === "checking") {
    return (
      <div className="py-20 max-w-md mx-auto text-center">
        <Loader2 className="mx-auto animate-spin text-[#76B82A]" size={48} />
        <h2 className="font-display uppercase text-3xl mt-6">Confirming payment...</h2>
        <p className="text-[#F5F2E8]/60 mt-2 font-mono text-sm">
          Hang tight — verifying with Stripe.
        </p>
      </div>
    );
  }

  if (status === "paid") {
    return (
      <div className="py-20 max-w-md mx-auto text-center" data-testid="payment-success">
        <CheckCircle2 className="mx-auto text-[#76B82A]" size={56} />
        <h2 className="font-display uppercase text-4xl mt-6">You're sponsoring the hunt.</h2>
        <p className="text-[#F5F2E8]/70 mt-3">
          ${data?.amount_total ? (data.amount_total / 100).toFixed(0) : "—"} received. Your sponsor profile is live on The Local Loot.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/sponsors" className="btn-loot">See sponsors wall</Link>
          <Link to="/home" className="btn-ghost">Back to home</Link>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="py-20 max-w-md mx-auto text-center" data-testid="payment-pending">
        <CheckCircle2 className="mx-auto text-[#D7A62A]" size={48} />
        <h2 className="font-display uppercase text-3xl mt-6">Payment received — syncing</h2>
        <p className="text-[#F5F2E8]/70 mt-3">
          Your checkout is complete. We're finalizing your sponsor profile — it will appear on the sponsors wall shortly.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/sponsors" className="btn-loot">See sponsors wall</Link>
          <Link to="/home" className="btn-ghost">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 max-w-md mx-auto text-center">
      <XCircle className="mx-auto text-[#E53935]" size={48} />
      <h2 className="font-display uppercase text-3xl mt-6">Payment not confirmed</h2>
      <p className="text-[#F5F2E8]/70 mt-3">
        We couldn't verify your payment. If you were charged, contact us — otherwise try again.
      </p>
      <button onClick={() => navigate("/sponsor-apply")} className="btn-loot mt-6">
        Back to packages
      </button>
    </div>
  );
}
